/**
 * Helpers for the `@perion-undertone/ut-react-common` `<DataGrid>` (which is
 * backed by `react-data-grid` v7).
 *
 * `react-data-grid` is declared as a direct dependency of this app (pinned to
 * the exact version `ut-react-common` uses, `7.0.0-beta.59`) so npm hoists a
 * single copy to the top-level `node_modules`. That keeps the bare specifier
 * below resolvable in every environment - local installs, CI, and the Docker
 * deploy build - regardless of npm's hoisting decisions.
 *
 * `adaptColumnsForV7` exists because most schema files in this codebase still
 * use the v6 column shape (`formatter`, `headerRenderer`, single `cellClass`).
 * v7 expects `renderCell` / `renderHeaderCell` and treats `cellClass` and
 * `headerCellClass` separately. The adapter translates the legacy shape on
 * the fly so individual schemas don't have to be rewritten in lockstep.
 */
export {
    SelectColumn,
    SELECT_COLUMN_KEY
} from 'react-data-grid';

/**
 * Convert a v6-style column array to v7. Columns that already use v7 props are
 * passed through unchanged, so it's safe to call on partially-migrated schemas.
 *
 * Legacy formatter signature:        ({value, row, column}) => ReactNode
 *   (v6 also passed dependentValues; this codebase does not use it.)
 * Legacy header renderer signature:  ({column}) => ReactNode
 *
 * Also mirrors a string `cellClass` onto `headerCellClass` when the latter
 * isn't explicitly set. v6 applied `cellClass` to both header and body cells;
 * v7 keeps them separate. This restores the previous behaviour without
 * forcing every schema file to declare both. Function-form `cellClass` (the
 * row-aware variant) is intentionally not mirrored - v7's
 * `headerCellClass` only accepts strings.
 */
export const adaptColumnsForV7 = (columns) =>
    (columns || []).map((col) => {
        if (!col) {
            return col;
        }
        const {formatter, headerRenderer, ...rest} = col;
        const next = {...rest};
        if (typeof formatter === 'function' && !next.renderCell) {
            next.renderCell = (props) => {
                const {row, column} = props || {};
                const value = row && column ? row[column.key] : undefined;
                return formatter({value, row, column});
            };
        }
        if (typeof headerRenderer === 'function' && !next.renderHeaderCell) {
            next.renderHeaderCell = (props) => headerRenderer({column: props.column});
        }
        if (typeof next.cellClass === 'string' && next.headerCellClass == null) {
            next.headerCellClass = next.cellClass;
        }
        return next;
    });
